import * as React from 'react';
import Modal from 'components/common/modal';
import strings from 'utils/strings';
import { killEvent } from 'utils/domUtils';
import addUserSvg from 'images/add_user.svg';
import UserDetails from 'types/userDetails';
import './addEditUserModal.scss';

interface AddEditUserModalProps {
    roles: UserRole[];                         // available user roles
    onCommit?: (details: UserDetails, sendInvite?: boolean)
        => void;                               // callback to notify parent of commit of dialog/value
    onHide?: () => void;                       // callback to notify parent when modal hidden
    show: boolean;                             // whether or not to make the modal visible
    userDetails?: UserDetails | null;
    sendInvite?: boolean;                      // whether or not to send the invite on creation
    currentAccountDetails?: MasterAccount;
}

// We need to export this for testing
export interface AddEditUserModalState {
    visible: boolean;    // whether or not modal is currently visible
    firstName: string;   // current firstName text input in form
    lastName: string;    // current lastName text input in form
    email: string;       // current email text input in form
    roles: string[];     // current roles
    sendInvite: boolean; // whether or not an invite should be sent at time of creation
}

class AddEditUserModal extends React.PureComponent<AddEditUserModalProps, AddEditUserModalState> {
    // reference to first input field to give it focus on show
    private _firstNameInput: HTMLInputElement | null;

    constructor(props: AddEditUserModalProps) {
        super(props);

        const roles = props.userDetails ?
            props.userDetails.roleIds :
            (props.roles && props.roles[0] ? [props.roles[0].id] : []);

        this.state = Object.assign(
            {},
            this.state,
            {
                visible: props.show,
                firstName: props.userDetails ? props.userDetails.firstName : '',
                lastName: props.userDetails ? props.userDetails.lastName : '',
                email: props.userDetails ? props.userDetails.email : '',
                roles,
                sendInvite: props.sendInvite ? props.sendInvite : true
            });

        // bind all our event handlers
        this._hide = this._hide.bind(this);
        this._onCommit = this._onCommit.bind(this);
        this._onFirstNameChange = this._onFirstNameChange.bind(this);
        this._onLastNameChange = this._onLastNameChange.bind(this);
        this._onEmailChange = this._onEmailChange.bind(this);
        this._onRoleChange = this._onRoleChange.bind(this);
        this._onSendInviteChange = this._onSendInviteChange.bind(this);
    }

    // sync our internal state with props
    UNSAFE_componentWillReceiveProps(nextProps: AddEditUserModalProps): void {
        const updateUserDetails = !nextProps.userDetails ||
            JSON.stringify(this.props.userDetails) !== JSON.stringify(nextProps.userDetails);

        const newState = {
            visible: nextProps.show
        };

        if (updateUserDetails) {
            const roles = nextProps.userDetails ?
            nextProps.userDetails.roleIds :
            (nextProps.roles && nextProps.roles[0] ? [nextProps.roles[0].id] : []);
            // update the dialog fields if they've changed
            Object.assign(newState, {
                firstName: nextProps.userDetails ? nextProps.userDetails.firstName : '',
                lastName: nextProps.userDetails ? nextProps.userDetails.lastName : '',
                email: nextProps.userDetails ? nextProps.userDetails.email : '',
                roles
            });
        }

        this.setState(newState);
    }

    componentDidUpdate(prevProps: AddEditUserModalProps, prevState: AddEditUserModalState): void {
        // give focus to first element on show of modal
        // (needs to be on timer due to...fade in reasons?)
        if (!prevState.visible && this.state.visible) {
            window.setTimeout(() => {
                if (this._firstNameInput) {
                    this._firstNameInput.focus();
                    this._firstNameInput.select();
                }
            }, 50);
        }
    }

    render(): React.ReactNode {
        const { userDetails, roles: availableRoles, currentAccountDetails } = this.props;
        const { visible, firstName, lastName, email } = this.state;

        // consider us editing a user if details were passed in
        const editMode = !!userDetails;
        return (
            <Modal className="add-edit-user-modal" show={visible} onHide={this._hide} >
                <div className="add-edit-user-modal__left-pane">
                    <h2 className="add-edit-user-modal__title">
                        {editMode ? strings.editUser : strings.addUser}
                    </h2>
                    <div className="user-detail">
                        <label htmlFor="firstname" className="user-detail__label">
                            {strings.userDetailsFirstName}
                        </label>
                        <input
                            className="pxl-form-control user-detail__input"
                            id="firstname"
                            onChange={this._onFirstNameChange}
                            ref={ref => this._firstNameInput = ref}
                            value={firstName || ''}
                        />
                    </div>
                    <div className="user-detail">
                        <label htmlFor="lastname" className="user-detail__label">
                            {strings.userDetailsLastName}
                        </label>
                        <input
                            className="pxl-form-control user-detail__input"
                            id="lastname"
                            onChange={this._onLastNameChange}
                            value={lastName || ''}
                        />
                    </div>
                    <div className="user-detail">
                        <label htmlFor="email" className="user-detail__label">
                            {strings.userDetailsEmail}
                        </label>
                        <input
                            className="pxl-form-control user-detail__input email"
                            disabled={editMode}
                            id="email"
                            onChange={this._onEmailChange}
                            type="email"
                            value={email || ''}
                        />
                    </div>
                    <div className="user-detail">
                        <label htmlFor="role" className="user-detail__label">
                            {strings.userDetailsRole}
                        </label>
                        <select
                            id="role"
                            className="pxl-form-control user-detail__select"
                            onChange={this._onRoleChange}
                            defaultValue={(userDetails
                                && userDetails.roleIds
                                && userDetails.roleIds[0]) ?? ''}
                        >
                            {
                                availableRoles
                                    .sort((a, b) => a.name ? a.name.localeCompare(b.name) : 1)
                                    .filter(this._isRoleAllowed)
                                    .map(role =>
                                        <option
                                            value={role.id}
                                            key={role.id}
                                        >
                                            {role.name}
                                        </option>)
                            }
                        </select>
                    </div>
                    {editMode || currentAccountDetails?.samlPartnerId ? null : <div className="send-invite">
                        <label htmlFor="sendInvite" className="user-detail__label">
                            {strings.sendEmail}
                        </label>
                        <select
                            defaultValue={strings.sendNow}
                            id="sendInvite"
                            className="pxl-form-control user-detail__select"
                            onChange={this._onSendInviteChange}
                        >
                            {[
                                <option
                                    value={'true'}
                                    key={'now'}
                                >
                                        {strings.sendNow}
                                </option>,
                                <option
                                    value={'false'}
                                    key={'later'}
                                >
                                        {strings.sendLater}
                                </option>
                            ]}
                        </select>
                    </div>}
                    <div className="modal__button-container">
                        <a className="modal__cancel-link" href="#" onClick={this._hide}>
                            {strings.cancel}
                        </a>
                        <button
                            className="pxl-btn pxl-btn-primary modal__ok-button"
                            onClick={this._onCommit}
                            disabled={!editMode && !(email && firstName && lastName)}
                        >
                            {editMode ? strings.save : strings.add}
                        </button>
                    </div>
                </div>
                <div className="add-edit-user-modal__right-pane">
                    <img src={addUserSvg} className="add-user-modal__graphic" />
                </div>
            </Modal>
        );
    }

    private _hide(e?: React.SyntheticEvent<HTMLAnchorElement>): void {
        if (e) {
            killEvent(e);
        }
        this.setState({
            visible: false
        });

        const { onHide } = this.props;
        if (onHide) {
            onHide();
        }
    }

    private _onCommit(): void {
        const { onCommit } = this.props;

        if (onCommit) {
            const { firstName, lastName, email, roles, sendInvite } = this.state;
            onCommit({
                firstName,
                lastName,
                email,
                roleIds: roles
            },
            sendInvite);
        }
        this._hide();
    }

    private _onFirstNameChange(e: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            firstName: e.target.value
        });
    }

    private _onLastNameChange(e: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            lastName: e.target.value
        });
    }

    private _onEmailChange(e: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            email: e.target.value
        });
    }

    private _onRoleChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        this.setState({
            roles: [e.target.value]
        });
    }

    private _onSendInviteChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        this.setState({
            sendInvite: e.target.value === 'true'
        });
    }

    private _isRoleAllowed(role: UserRole): boolean {
        return role.name === 'Manager'
            || role.name === 'HR Admin'
            || role.name === 'HRBP';
    }
}

export { AddEditUserModal };
export default AddEditUserModal;



// WEBPACK FOOTER //
// ./src/components/addEditUserModal.tsx